import React from 'react';
import {
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  ChartBarIcon,
  CalendarIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ArrowPathIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { DocumentAnalytics as DocumentAnalyticsType, DocumentDetail, DocumentVersion } from '../types/documentVersion.types';
import { formatVersionLabel } from '../utils/versionUtils';

interface DocumentAnalyticsProps {
  analytics: DocumentAnalyticsType;
  document?: DocumentDetail | null;
  versions?: DocumentVersion[];
  onViewTracking?: () => void;
}

const formatBytes = (bytes?: number) => {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(value < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

const countTrackedRevisions = (versions: DocumentVersion[]) => {
  return versions.reduce((sum, version) => {
    const summary = version.checkin_metadata?.tracking_summary as any;
    const revisionSummary = summary?.revisionSummary;
    const explicitCount = revisionSummary?.totalRevisions ?? revisionSummary?.trackedRevisionCount ?? summary?.trackedRevisionCount;
    if (Number.isFinite(Number(explicitCount))) return sum + Number(explicitCount);
    const revisions = revisionSummary?.revisions || revisionSummary?.samples;
    return sum + (Array.isArray(revisions) ? revisions.length : 0);
  }, 0);
};

export const DocumentAnalytics: React.FC<DocumentAnalyticsProps> = ({
  analytics,
  document,
  versions = [],
  onViewTracking,
}) => {
  const latestVersion = versions[0];
  const previousVersion = versions[1];
  const latestVersionLabel = latestVersion ? formatVersionLabel(latestVersion.version_number) : 'None';
  const signedVersions = versions.filter((version) => Boolean(version.checkin_metadata?.signature_audit)).length;
  const forceCheckins = versions.filter((version) => Boolean(version.checkin_metadata?.force_checkin)).length;
  const workingCopyVersions = versions.filter((version) => Boolean(version.checkin_metadata?.working_copy_saved)).length;
  const autosaveCount = versions.reduce((sum, version) => sum + Number(version.checkin_metadata?.autosave_count || 0), 0);
  const trackedRevisionCount = countTrackedRevisions(versions);
  const wordVersionCount = versions.filter((version) => {
    const haystack = [
      version.changes_description,
      version.version_comment,
      version.checkin_metadata?.device_info,
    ].join(' ').toLowerCase();
    return haystack.includes('microsoft word') || haystack.includes('word add-in') || haystack.includes('word');
  }).length;
  const latestSize = Number(latestVersion?.size_bytes || 0);
  const previousSize = Number(previousVersion?.size_bytes || 0);
  const sizeDeltaPercent = previousSize > 0 ? Math.round(((latestSize - previousSize) / previousSize) * 100) : 0;
  const hasLargeSizeSwing = previousSize > 0 && Math.abs(sizeDeltaPercent) >= 50;
  const hasWorkingCopy = Boolean(document?.last_local_save_at || document?.working_copy_metadata?.unsaved_changes);
  const isCheckedOut = Boolean(document?.is_checked_out);

  const reviewFlags = [
    isCheckedOut ? 'Document is currently checked out.' : null,
    hasWorkingCopy ? 'Local/working-copy save exists; confirm official DMS version is current.' : null,
    forceCheckins > 0 ? `${forceCheckins} force check-in event${forceCheckins === 1 ? '' : 's'} detected.` : null,
    trackedRevisionCount > 0 ? `${trackedRevisionCount} tracked Word revision${trackedRevisionCount === 1 ? '' : 's'} captured.` : null,
    hasLargeSizeSwing ? `Latest version size changed ${sizeDeltaPercent > 0 ? '+' : ''}${sizeDeltaPercent}% from prior version.` : null,
  ].filter(Boolean) as string[];

  const readiness = reviewFlags.length === 0
    ? { label: 'Ready', tone: 'green', summary: 'No obvious version, checkout, or tracking issues detected from available metadata.' }
    : reviewFlags.length <= 2
      ? { label: 'Review', tone: 'amber', summary: 'A short attorney review is recommended before relying on this document.' }
      : { label: 'Attention', tone: 'red', summary: 'Multiple document lifecycle signals need attorney review.' };

  const readinessClasses = readiness.tone === 'green'
    ? 'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800'
    : readiness.tone === 'amber'
      ? 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800'
      : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800';

  const intelligenceCards = [
    {
      label: 'Official Version',
      value: latestVersionLabel,
      detail: `${analytics.totalVersions} total version${analytics.totalVersions === 1 ? '' : 's'}`,
      icon: DocumentTextIcon,
      color: 'text-lex-accent',
    },
    {
      label: 'Last Modified',
      value: new Date(analytics.lastModified).toLocaleDateString(),
      detail: analytics.documentAge,
      icon: ClockIcon,
      color: 'text-green-600',
    },
    {
      label: 'Chain of Custody',
      value: forceCheckins > 0 ? `${forceCheckins} force event${forceCheckins === 1 ? '' : 's'}` : 'Normal',
      detail: `${wordVersionCount} Word-sourced, ${workingCopyVersions} working-copy save${workingCopyVersions === 1 ? '' : 's'}`,
      icon: ShieldCheckIcon,
      color: forceCheckins > 0 ? 'text-red-600' : 'text-emerald-600',
    },
    {
      label: 'Review Signals',
      value: trackedRevisionCount > 0 ? `${trackedRevisionCount} redline${trackedRevisionCount === 1 ? '' : 's'}` : 'No redlines captured',
      detail: `${signedVersions} signed/audited version${signedVersions === 1 ? '' : 's'}`,
      icon: SparklesIcon,
      color: trackedRevisionCount > 0 ? 'text-amber-600' : 'text-purple-600',
    },
    {
      label: 'Collaboration State',
      value: isCheckedOut ? 'Checked out' : 'Available',
      detail: hasWorkingCopy ? 'Working-copy signal present' : 'No working-copy signal',
      icon: isCheckedOut ? LockClosedIcon : CheckCircleIcon,
      color: isCheckedOut || hasWorkingCopy ? 'text-amber-600' : 'text-green-600',
    },
    {
      label: 'Version Size',
      value: formatBytes(latestSize || analytics.averageVersionSize),
      detail: previousVersion ? `${sizeDeltaPercent > 0 ? '+' : ''}${sizeDeltaPercent}% from ${formatVersionLabel(previousVersion.version_number)}` : 'Latest available size',
      icon: ChartBarIcon,
      color: hasLargeSizeSwing ? 'text-orange-600' : 'text-lex-accent',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Document Intelligence</h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Lawyer-facing interpretation of version history, checkout state, Word/DMS activity, and audit signals.
            </p>
          </div>
          {onViewTracking && (
            <button
              type="button"
              onClick={onViewTracking}
              className="inline-flex items-center gap-2 rounded-lg border border-lex-accent/30 px-3 py-2 text-sm font-medium text-lex-accent hover:bg-lex-accent-soft"
            >
              <ArrowPathIcon className="h-4 w-4" />
              View full tracking log
            </button>
          )}
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${readinessClasses}`}>
        <div className="flex items-start gap-3">
          {readiness.tone === 'green' ? (
            <CheckCircleIcon className="h-6 w-6 flex-shrink-0" />
          ) : (
            <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0" />
          )}
          <div>
            <div className="text-sm font-semibold uppercase tracking-wide">Review Readiness: {readiness.label}</div>
            <p className="mt-1 text-sm">{readiness.summary}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {intelligenceCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon className={`h-5 w-5 ${card.color}`} />
                <span className="text-sm font-medium text-gray-900 dark:text-white">{card.label}</span>
              </div>
              <p className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">{card.value}</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{card.detail}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
          <div className="flex items-center gap-2">
            <UserIcon className="h-5 w-5 text-purple-600" />
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Ownership</h5>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Created by {analytics.createdBy}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">Most active editor: {analytics.mostActiveEditor}</p>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-red-600" />
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Velocity</h5>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{analytics.versionFrequency}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">Autosaves captured: {autosaveCount}</p>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-lex-accent" />
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Storage</h5>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Average version size: {formatBytes(analytics.averageVersionSize)}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">Latest version size: {formatBytes(latestSize)}</p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white">Attorney Review Notes</h5>
        <div className="mt-3 space-y-2">
          {(reviewFlags.length > 0 ? reviewFlags : ['No immediate review flags from available document metadata.']).map((flag) => (
            <div key={flag} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-lex-accent" />
              <span>{flag}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
